import { FormEvent, ReactNode, useEffect, useId, useMemo, useState } from "react";

import { HerdDepartureBadge } from "../breeding/HerdDepartureBadge";
import { EarTag } from "../tags/EarTag";
import { Field } from "./Field";
import { FormModal } from "../modals/FormModal";
import { SEX_LABELS } from "../../lib/format";
import { passwordFieldProps } from "../../lib/inputPrivacy";
import { tagMatchesFilter } from "../../lib/tagVerificationInput";
import type { BreedingMember, EligibleAnimal } from "../../types";

export type SeasonMemberDraft = {
  tag: string;
  role: BreedingMember["role"];
};

type RosterMember = SeasonMemberDraft & {
  left_herd_on?: string | null;
  left_herd_status?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  title?: string;
  members: RosterMember[];
  eligible: EligibleAnimal[];
  busy?: boolean;
  header?: ReactNode;
  testId?: string;
  testIdPrefix?: string;
  onSave: (members: SeasonMemberDraft[], password: string) => void | Promise<void>;
};

function composeMembers(
  current: SeasonMemberDraft[],
  kept: Set<string>,
  picked: Set<string>,
  eligible: EligibleAnimal[],
): SeasonMemberDraft[] {
  const byTag = new Map(eligible.map((animal) => [animal.tag, animal]));
  const members: SeasonMemberDraft[] = [];
  for (const member of current) {
    if (kept.has(member.tag)) {
      members.push({ tag: member.tag, role: member.role });
    }
  }
  for (const tag of picked) {
    if (kept.has(tag) || members.some((member) => member.tag === tag)) {
      continue;
    }
    const animal = byTag.get(tag);
    members.push({
      tag,
      role: animal?.sex === "male" ? "sire" : "dam",
    });
  }
  return members;
}

export function SeasonMemberPicker({
  open,
  onClose,
  title = "Edytuj grupę",
  members,
  eligible,
  busy = false,
  header,
  testId = "breeding-members-form",
  testIdPrefix = "breeding",
  onSave,
}: Props) {
  const fieldNs = useId().replace(/:/g, "");
  const [memberQuery, setMemberQuery] = useState("");
  const [sexFilter, setSexFilter] = useState<"all" | "female" | "male">("all");
  const [keptMembers, setKeptMembers] = useState<Set<string>>(new Set());
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [password, setPassword] = useState("");

  const ewes = useMemo(
    () => members.filter((member) => member.role === "dam"),
    [members],
  );
  const rams = useMemo(
    () => members.filter((member) => member.role === "sire"),
    [members],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setMemberQuery("");
    setSexFilter("all");
    setKeptMembers(new Set(members.map((member) => member.tag)));
    setPicked(new Set());
    setPassword("");
  }, [open]);

  const filteredEligible = useMemo(() => {
    const q = memberQuery.trim();
    const existing = new Set(members.map((member) => member.tag));
    return eligible.filter((animal) => {
      if (existing.has(animal.tag)) {
        return false;
      }
      if (sexFilter === "female" && animal.sex !== "female") {
        return false;
      }
      if (sexFilter === "male" && animal.sex !== "male") {
        return false;
      }
      return !q || tagMatchesFilter(animal.tag, q);
    });
  }, [eligible, memberQuery, members, sexFilter]);

  function toggleKept(tag: string) {
    setKeptMembers((current) => {
      const next = new Set(current);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  }

  function togglePicked(tag: string) {
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(tag)) {
        next.delete(tag);
      } else {
        next.add(tag);
      }
      return next;
    });
  }

  function selectAllEligible() {
    setPicked((current) => {
      const next = new Set(current);
      for (const animal of filteredEligible) {
        next.add(animal.tag);
      }
      return next;
    });
  }

  function clearPickedEligible() {
    setPicked((current) => {
      const next = new Set(current);
      for (const animal of filteredEligible) {
        next.delete(animal.tag);
      }
      return next;
    });
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const assembled = composeMembers(members, keptMembers, picked, eligible);
    await onSave(assembled, password);
  }

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={title}
      size="xl"
      testId={testId}
      className="breeding-edit-modal"
      onSubmit={(event) => void onSubmit(event)}
    >
      {header}
      {members.length > 0 ? (
        <section className="breeding-edit-section">
          <div className="breeding-edit-section__header">
            <h4 className="breeding-edit-section__title">Obecny skład</h4>
            <p className="muted">
              Odznacz, aby usunąć ze składu · zostaje {keptMembers.size} z {members.length}
            </p>
          </div>
          <div className="breeding-roster">
            <div className="breeding-roster__col">
              <p className="breeding-roster__label">
                Owce ({ewes.filter((member) => keptMembers.has(member.tag)).length}/
                {ewes.length})
              </p>
              <div className="breeding-tag-grid" data-testid={`${testIdPrefix}-current-members`}>
                {ewes.map((member) => (
                  <label
                    key={member.tag}
                    className={
                      keptMembers.has(member.tag)
                        ? "breeding-tag-chip"
                        : "breeding-tag-chip breeding-tag-chip--off"
                    }
                  >
                    <input
                      type="checkbox"
                      checked={keptMembers.has(member.tag)}
                      onChange={() => toggleKept(member.tag)}
                    />
                    <span className="breeding-tag-chip__tag">
                      <EarTag value={member.tag} />
                    </span>
                    <HerdDepartureBadge
                      leftOn={member.left_herd_on}
                      status={member.left_herd_status}
                    />
                  </label>
                ))}
                {ewes.length === 0 ? (
                  <p className="muted breeding-roster__empty">Brak owiec</p>
                ) : null}
              </div>
            </div>
            <div className="breeding-roster__col">
              <p className="breeding-roster__label">
                Tryki ({rams.filter((member) => keptMembers.has(member.tag)).length}/
                {rams.length})
              </p>
              <div className="breeding-tag-grid">
                {rams.map((member) => (
                  <label
                    key={member.tag}
                    className={
                      keptMembers.has(member.tag)
                        ? "breeding-tag-chip"
                        : "breeding-tag-chip breeding-tag-chip--off"
                    }
                  >
                    <input
                      type="checkbox"
                      checked={keptMembers.has(member.tag)}
                      onChange={() => toggleKept(member.tag)}
                    />
                    <span className="breeding-tag-chip__tag">
                      <EarTag value={member.tag} />
                    </span>
                    <HerdDepartureBadge
                      leftOn={member.left_herd_on}
                      status={member.left_herd_status}
                    />
                  </label>
                ))}
                {rams.length === 0 ? (
                  <p className="muted breeding-roster__empty">Brak tryków</p>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="breeding-edit-section">
        <div className="breeding-edit-section__header">
          <h4 className="breeding-edit-section__title">Dodaj zwierzęta</h4>
          <p className="muted">
            {picked.size > 0 ? `Wybrane do dodania: ${picked.size}` : "Wybierz kolczyki"}
          </p>
        </div>
        <div className="breeding-add-toolbar">
          <input
            className="input breeding-add-toolbar__search"
            type="search"
            placeholder="Szukaj kolczyka…"
            value={memberQuery}
            onChange={(event) => setMemberQuery(event.target.value)}
            data-testid={`${testIdPrefix}-member-search`}
          />
          <div className="actions breeding-sex-filters" role="group" aria-label="Filtr płci">
            {(
              [
                ["all", "Wszystkie"],
                ["female", "Owce"],
                ["male", "Tryki"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={
                  sexFilter === value ? "btn btn--sm" : "btn btn--ghost btn--sm"
                }
                data-testid={`${testIdPrefix}-sex-filter-${value}`}
                onClick={() => setSexFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            data-testid={`${testIdPrefix}-select-visible`}
            onClick={selectAllEligible}
            disabled={filteredEligible.length === 0}
          >
            Zaznacz wszystkie
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={clearPickedEligible}
            disabled={filteredEligible.every((animal) => !picked.has(animal.tag))}
          >
            Odznacz
          </button>
        </div>
        <p className="muted breeding-edit-section__hint">
          Wyników: {filteredEligible.length}
          {memberQuery.trim() ? " (po filtrze)" : ""}
          {sexFilter !== "all" ? ` · ${sexFilter === "female" ? "owce" : "tryki"}` : ""}
        </p>
        <div
          className="breeding-tag-grid breeding-tag-grid--add"
          data-testid={`${testIdPrefix}-eligible-list`}
        >
          {filteredEligible.map((animal) => (
            <label
              key={animal.tag}
              className={
                picked.has(animal.tag)
                  ? "breeding-tag-chip breeding-tag-chip--picked"
                  : "breeding-tag-chip"
              }
            >
              <input
                type="checkbox"
                checked={picked.has(animal.tag)}
                onChange={() => togglePicked(animal.tag)}
              />
              <span className="breeding-tag-chip__tag">
                <EarTag value={animal.tag} />
              </span>
              <HerdDepartureBadge leftOn={animal.left_herd_on} status={animal.status} />
              <span className="breeding-tag-chip__meta">
                {SEX_LABELS[animal.sex] || animal.sex}
              </span>
            </label>
          ))}
          {filteredEligible.length === 0 ? (
            <p className="muted breeding-roster__empty">
              Brak zwierząt do dodania
              {memberQuery.trim() ? " dla tego wyszukiwania" : ""}.
            </p>
          ) : null}
        </div>
      </section>

      <section className="breeding-edit-section breeding-edit-section--footer">
        <Field label="Hasło administratora">
          <input
            className="input"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            data-testid={`${testIdPrefix}-members-password`}
            {...passwordFieldProps(`${fieldNs}-${testIdPrefix}-members-password`)}
          />
        </Field>
        <div className="modal__actions">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={onClose}
            data-testid={`${testIdPrefix}-members-cancel`}
          >
            Anuluj
          </button>
          <button
            className="btn"
            type="submit"
            disabled={busy}
            data-testid={`${testIdPrefix}-members-save`}
          >
            Zapisz grupę ({keptMembers.size + picked.size})
          </button>
        </div>
      </section>
    </FormModal>
  );
}
